type TokenType = keyof typeof TokenType

const TokenType = {
  newline: /(\n)/,
  whitespace: /(\s+?)/,
  native: /`([a-z]+)`/,
  string: /('[^']*')/,
  special: /(M|S)/,
  op2: /(floor|pick|slide|to_audio)/,
  id: /([a-zA-Z]+[a-zA-Z0-9_]*)/,
  number: /([0-9]+[dhk][0-9]*|[0-9]+\.[0-9]*|\.?[0-9]+)/,
  blockcomment: /(\[;|\(;|{;)/,
  op: /(\+=|\+|-|\*|\/|\\|\^|%|!|=|@|\{|\}|\(|\)|\[|\]|:|\.|,)/,
  comment: /(;[^\n]*)/,
  any: /(.+?)/,
}

const typesEntries = Object.entries(TokenType).map(([x, y]) => [x.replace(/[^a-z]+/g, ''), y]) as [TokenType, RegExp][]

const regexp = new RegExp(
  typesEntries.map(([, x]) => x.source).join('|'),
  'g'
)

export interface Token<T = TokenType> {
  /** Token type. */
  type: T
  /** The text representation of the token. */
  text: string
  /** Zero based line number. */
  line: number
  /** Zero based column number. */
  col: number
  /** Rightmost column number, zero based. */
  right: number
  /** Bottom line, zero based. */
  bottom: number
  /** Zero based index. */
  index: number

  stack?: unknown[]
  tokens: unknown[]

  /** Arbitrary metadata object. */
  meta: Record<string, any>
}

export const Close = {
  '[': ']',
  '(': ')',
  '{': '}',
} as const

export function* tokenize(code: string): Generator<Token, void, unknown> {
  let i = 0
  let text: string | null
  let type: Token['type']
  let line = 0
  let lineIndex = 0
  let depth = 0
  let col = 0
  const length = typesEntries.length + 1
  let match: any
  let slice: string
  const it = code.matchAll(regexp)
  outer: for (match of it) {
    for (i = 1; i < length; i++) {
      text = match[i]
      if (text != null) {
        type = typesEntries[i - 1][0]
        switch (type) {
          case 'whitespace':
            break
          case 'newline':
            ++line
            lineIndex = match.index! + 1
            break
          case 'blockcomment': {
            type = 'comment'
            let begin = match
            const open = text[0] as keyof typeof Close
            const close = Close[open]
            depth = 1
            for (const m of it) {
              for (let x = 0, t: string; x < length; x++) {
                t = m[x]
                if (t != null) {
                  if (t === '\n') {
                    col = begin.index! - lineIndex
                    slice = code.slice(begin.index, m.index!)
                    yield {
                      type,
                      text: slice,
                      line,
                      col,
                      right: col + slice.length,
                      bottom: line,
                      index: begin.index!,
                      // stack: [],
                      tokens: [],
                      meta: {}
                    }
                    begin = m

                    ++line
                    lineIndex = m.index! + 1
                  }
                  else if (t === open) depth++
                  else if (t === close) {
                    if (!--depth) {
                      col = begin.index! - lineIndex
                      slice = code.slice(begin.index, m.index! + 1)
                      yield {
                        type,
                        text: slice,
                        line,
                        col,
                        right: col + slice.length,
                        bottom: line,
                        index: begin.index!,
                        // stack: [],
                        tokens: [],
                        meta: {}
                      }
                      continue outer
                    }
                  }
                  break
                }
              }
            }
            break
          }
          default:
            col = match.index! - lineIndex
            yield {
              type,
              text,
              line,
              col,
              right: col + text.length,
              bottom: line,
              index: match.index,
              // stack: [],
              tokens: [],
              meta: {}
            } as Token
        }
        break
      }
    }
  }
}

export function test() {
  // @env browser
  describe('tokenize', () => {
    const passing: [string, Token[]][] = [
      ['hello world', [
        { type: 'id', text: 'hello', line: 0, col: 0, right: 5, bottom: 0, index: 0, stack: [], tokens: [], meta: {} },
        { type: 'id', text: 'world', line: 0, col: 6, right: 11, bottom: 0, index: 6, stack: [], tokens: [], meta: {} },
      ]],

      ['hello\nworld', [
        { type: 'id', text: 'hello', line: 0, col: 0, right: 5, bottom: 0, index: 0, stack: [], tokens: [], meta: {} },
        { type: 'id', text: 'world', line: 1, col: 0, right: 5, bottom: 1, index: 6, stack: [], tokens: [], meta: {} },
      ]],

      ['hello\nwo\n  rld', [
        { type: 'id', text: 'hello', line: 0, col: 0, right: 5, bottom: 0, index: 0, stack: [], tokens: [], meta: {} },
        { type: 'id', text: 'wo', line: 1, col: 0, right: 2, bottom: 1, index: 6, stack: [], tokens: [], meta: {} },
        { type: 'id', text: 'rld', line: 2, col: 2, right: 5, bottom: 2, index: 11, stack: [], tokens: [], meta: {} },
      ]],
    ]
    passing.forEach(([code, expected]) => {
      it(code, () => {
        expect([...tokenize(code)]).toMatchObject(expected)
      })
    })
  })
}
